import './settings-leaf.css';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Company } from '@shared/types/index.ts';
import { areaPricingRulesSchema, areaRatePrecisionProblem } from '@shared/area-pricing.ts';
import { useSession } from '../../state/session.tsx';
import { Button, Checkbox } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';

const snapshot = (company: Company) => ({
  areaPricingRules: company.areaPricingRules ?? null,
  areaPricingCurrencyCode: company.currencyCode,
});
const payload = (draft: ReturnType<typeof snapshot>) => ({
  areaPricingRules: draft.areaPricingRules,
  areaPricingCurrencyCode: draft.areaPricingCurrencyCode,
});

export default function AreaPricingSettingsCard() {
  const { company } = useSession();
  return <AreaPricingCurrencyForm key={`${company?.id}:${company?.currencyCode}`} />;
}

function AreaPricingCurrencyForm() {
  const { t } = useTranslation();
  const { company, refresh } = useSession();
  const form = useCompanyCardForm(snapshot, payload);
  const [validationRequested, setValidationRequested] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const rules = form.draft?.areaPricingRules;
  if (!company || !form.draft) return null;
  const checked = rules == null ? null : areaPricingRulesSchema.safeParse(rules);
  const valid =
    rules == null ||
    (checked?.success === true && areaRatePrecisionProblem(checked.data, company.currencyCode) === null);
  const error =
    (validationRequested && !valid) || Object.keys(form.fieldErrors).length > 0
      ? t('areaPricing.settingsInvalid')
      : form.error
        ? t('areaPricing.saveIssue')
        : null;
  const footerForm = {
    ...form,
    error,
    saving: form.saving || refreshing,
    submit: () => {
      setValidationRequested(true);
      if (valid) form.submit();
    },
  };
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="area-settings-heading">
      <div className="work-section-heading">
        <h2 id="area-settings-heading" className="mb-3 text-lg font-semibold">
          {t('areaPricing.settingsHeading')}
        </h2>
      </div>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('areaPricing.settingsHint')}</p>
      <Checkbox
        label={t('areaPricing.enable')}
        checked={rules != null}
        disabled={form.readOnly || refreshing}
        onChange={(event) =>
          form.set(
            'areaPricingRules',
            event.target.checked
              ? {
                  thresholdSquareFeet: 0,
                  defaultRateCents: 0,
                  minRateCents: 0,
                  maxRateCents: 0,
                }
              : null,
          )
        }
      />
      {rules && (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <NumberInput
            label={t('areaPricing.threshold')}
            value={rules.thresholdSquareFeet}
            blankValue={0}
            min={1}
            max={1_000_000}
            disabled={form.readOnly || refreshing}
            onValueChange={(value) => form.set('areaPricingRules', { ...rules, thresholdSquareFeet: value ?? 0 })}
          />
          {(['defaultRateCents', 'minRateCents', 'maxRateCents'] as const).map((field) => (
            <MoneyInput
              key={field}
              label={t(`areaPricing.${field}`)}
              currencyCode={company.currencyCode}
              valueCents={rules[field] || null}
              min={0}
              disabled={form.readOnly || refreshing}
              onChangeCents={(value) => form.set('areaPricingRules', { ...rules, [field]: value ?? 0 })}
            />
          ))}
        </div>
      )}
      <CardSaveFooter form={footerForm} saveLabel={t('settings.savePricing')} />
      {form.error && (
        <Button
          className="mt-3"
          busy={refreshing}
          disabled={form.saving}
          onClick={async () => {
            setRefreshing(true);
            try {
              await refresh();
            } catch {
              /* The save alert remains available for retry. */
            } finally {
              setRefreshing(false);
            }
          }}
        >
          {t('areaPricing.refreshCompany')}
        </Button>
      )}
    </section>
  );
}
